import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { UserTelemetryService } from '@customer-portal/core';
import {
  GlobalLanguagesStoreService,
  GlobalServiceMasterStoreService,
  GlobalSiteMasterStoreService,
} from '@customer-portal/data-access/global';
import {
  ProfileStoreService,
  SettingsCoBrowsingStoreService,
  SettingsCompanyDetailsStoreService,
  SettingsUserValidationService,
  UserValidation,
} from '@customer-portal/data-access/settings';
import { environment } from '@customer-portal/environments';
import {
  AppPagesEnum,
  AuthTokenConstants,
  RouteConfig,
} from '@customer-portal/shared/constants';
import {
  AuthService,
  CoBrowsingCompanyService,
  CoBrowsingSharedService,
} from '@customer-portal/shared/services';

import { UserValidationSubcodes } from '../constants';

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  private userTelemetryService = inject(UserTelemetryService);
  private readonly coBrowsingCompanyService = inject(CoBrowsingCompanyService);

  constructor(
    public settingsCompanyDetailsStoreService: SettingsCompanyDetailsStoreService,
    private authService: AuthService,
    private settingsUserValidationService: SettingsUserValidationService,
    private settingsCoBrowsingStoreService: SettingsCoBrowsingStoreService,
    private profileStoreService: ProfileStoreService,
    private router: Router,
    private coBrowsingSharedService: CoBrowsingSharedService,
    private globalSiteMasterStoreService: GlobalSiteMasterStoreService,
    private globalServiceMasterStoreService: GlobalServiceMasterStoreService,
    private globalLanguagesStoreService: GlobalLanguagesStoreService,
  ) {}

  initializePermissions = () => {
    this.clearHistoryState();

    return this.authService.isUserAuthenticatedWithExpiryInfo().pipe(
      catchError(() => {
        this.handleLogoutAndNavigateError('Authentication');

        return EMPTY;
      }),
      switchMap((auth) => {
        if (!auth.isUserAuthenticated) {
          if (this.authService.isLogOutInProgress()) {
            return of(true);
          }

          this.authService.logout().pipe(take(1)).subscribe();

          return from(this.router.navigateByUrl(AppPagesEnum.Welcome));
        }

        this.updateTokenExpiry(new Date(auth.expiryTimeUtc));

        return this.settingsUserValidationService.getUserValidation().pipe(
          catchError(() => {
            this.handleLogoutAndNavigateError('User Validation');

            return EMPTY;
          }),
          switchMap((result) => {
            if (!result) {
              return EMPTY;
            }

            if (
              result.isDnvUser &&
              !this.coBrowsingSharedService.getCoBrowsingUserEmail()
            ) {
              this.settingsCoBrowsingStoreService.updateIsDnvUser(true);
              this.settingsCoBrowsingStoreService.updateDnvUserAccessType(
                result.accessType,
              );
              this.userTelemetryService.initializeUserTracking(result);

              return this.loadInitialGlobalLanguagesList().pipe(
                switchMap((success) =>
                  success
                    ? from(
                        this.router.navigateByUrl(
                          AppPagesEnum.CoBrowsingCompanySelect,
                          {
                            state: { isUserValidated: true },
                          },
                        ),
                      )
                    : EMPTY,
                ),
              );
            }

            return this.handleUserValidationActions(result).pipe(
              tap(() => this.loadInitialData()),
              switchMap(() =>
                this.checkLoadedStates().pipe(
                  switchMap(() => this.checkErrors()),
                  switchMap((success) =>
                    success
                      ? this.loadApplicationData().pipe(
                          tap(() => {
                            if (
                              this.coBrowsingSharedService.getCoBrowsingUserEmail()
                            ) {
                              this.initializeCoBrowsingSettings();
                            }
                          }),
                        )
                      : EMPTY,
                  ),
                ),
              ),
            );
          }),
        );
      }),
    );
  };

  logoutIfAuthenticated(): Observable<any> {
    return this.authService.isUserAuthenticatedWithExpiryInfo().pipe(
      take(1),
      switchMap((auth) => {
        if (auth.isUserAuthenticated) {
          return this.authService
            .logout()
            .pipe(tap(() => this.authService.clearTokenData()));
        }

        return of(true);
      }),
    );
  }

  private initializeCoBrowsingSettings(): void {
    const dnvUserAccessType =
      this.coBrowsingSharedService.getCoBrowsingDnvUserAccess();
    const selectedCompany =
      this.coBrowsingCompanyService.getCobrowsingCompany();
    this.settingsCoBrowsingStoreService.updateIsDnvUser(true);

    if (dnvUserAccessType) {
      this.settingsCoBrowsingStoreService.updateDnvUserAccessType(
        dnvUserAccessType,
      );
    }

    if (selectedCompany) {
      this.settingsCoBrowsingStoreService.updateSelectedCoBrowsingCompany(
        selectedCompany,
      );
    }
  }

  private clearHistoryState(): void {
    if (
      window.history.state &&
      ('message' in window.history.state ||
        'isUserValidated' in window.history.state)
    ) {
      window.history.replaceState(null, '');
    }
  }

  private loadInitialData(): void {
    this.profileStoreService.loadProfileData();
    this.globalSiteMasterStoreService.loadGlobalSiteMasterList();
    this.globalServiceMasterStoreService.loadGlobalServiceMasterList();
    this.globalLanguagesStoreService.loadGlobalLanguagesList();
  }

  private loadInitialGlobalLanguagesList(): Observable<boolean> {
    this.globalLanguagesStoreService.loadGlobalLanguagesList();

    return merge(
      this.handleGlobalLanguagesError(),
      this.handleGlobalLanguagesLoaded(),
    ).pipe(take(1));
  }

  private handleGlobalLanguagesError(): Observable<boolean> {
    return this.globalLanguagesStoreService.languagesListLoadingError$.pipe(
      filter((error) => !!error),
      take(1),
      tap(() =>
        this.router.navigate([RouteConfig.Error.path], {
          state: { message: 'initialization' },
        }),
      ),
      map(() => false),
    );
  }

  private handleGlobalLanguagesLoaded(): Observable<boolean> {
    return this.globalLanguagesStoreService.languagesListLoaded$.pipe(
      filter((loaded) => loaded),
      take(1),
      switchMap(() => this.handleGlobalLanguagesLoadResult()),
    );
  }

  private handleGlobalLanguagesLoadResult(): Observable<boolean> {
    return this.globalLanguagesStoreService.languagesListLoadingError$.pipe(
      take(1),
      map((error) => {
        if (error) {
          this.router.navigate([RouteConfig.Error.path], {
            state: { message: 'initialization' },
          });

          return false;
        }

        return true;
      }),
    );
  }

  private checkLoadedStates(): Observable<boolean> {
    const loadedStates$ = {
      profileLoaded: this.profileStoreService.profileDataLoaded$,
      siteLoaded: this.globalSiteMasterStoreService.siteMasterLoaded$,
      serviceLoaded: this.globalServiceMasterStoreService.serviceMasterLoaded$,
      languagesLoaded: this.globalLanguagesStoreService.languagesListLoaded$,
    };

    return combineLatest(loadedStates$).pipe(
      filter(this.allStatesLoaded),
      take(1),
      map(() => true),
    );
  }

  private allStatesLoaded = (states: Record<string, boolean>): boolean =>
    Object.values(states).every((loaded) => loaded);

  private checkErrors(): Observable<boolean> {
    const errorStates$ = {
      profileError: this.profileStoreService.profileDataError$,
      companyError:
        this.settingsCompanyDetailsStoreService.companyDetailsError$,
      siteError: this.globalSiteMasterStoreService.siteMasterLoadingError$,
      serviceError:
        this.globalServiceMasterStoreService.serviceMasterLoadingError$,
      languagesError:
        this.globalLanguagesStoreService.languagesListLoadingError$,
    };

    return combineLatest(errorStates$).pipe(take(1), map(this.handleErrors));
  }

  private handleErrors = (errors: Record<string, any>): boolean => {
    const hasError = Object.values(errors).some((error) => error);

    if (hasError) {
      this.router.navigate([RouteConfig.Error.path], {
        state: { message: 'initialization' },
      });

      return false;
    }

    return true;
  };

  private loadApplicationData(): Observable<boolean> {
    return forkJoin({
      permissions: this.profileStoreService.profileInformationAccessLevel.pipe(
        filter(
          (accessLevel) => accessLevel && !!Object.keys(accessLevel).length,
        ),
        take(1),
      ),
    }).pipe(
      switchMap(() => this.userTelemetryService.initializeUserTracking()),
      map(() => true),
      catchError(() => {
        this.router.navigate([RouteConfig.Error.path]);

        return EMPTY;
      }),
    );
  }

  private handleUserValidationActions(result: UserValidation) {
    const {
      userIsActive,
      policySubCode,
      termsAcceptanceRedirectUrl,
      isAdmin,
      lastLoginTime,
      activatedInVeracity,
    } = result;

    if (activatedInVeracity === false) {
      return this.redirectToWelcome({ activatedInVeracity });
    }

    if (this.hasNoValidSubscription(userIsActive, policySubCode)) {
      return this.redirectToWelcome({ isUserValidated: false });
    }

    if (
      policySubCode === UserValidationSubcodes.TermsAndConditionsNotAccepted
    ) {
      const bypassToken = this.authService.getBypassToken();

      const returnUrl = bypassToken
        ? `${environment.baseUrl}?bypass=${bypassToken}#idp`
        : `${environment.baseUrl}#idp`;

      const termsAcceptanceReturnUrl = encodeURIComponent(returnUrl);

      const termsAcceptanceRedirectUpdatedUrl =
        termsAcceptanceRedirectUrl.replace(
          'redirect-url=',
          `redirect-url=${termsAcceptanceReturnUrl}`,
        );

      window.open(termsAcceptanceRedirectUpdatedUrl, '_self');

      return EMPTY;
    }

    this.settingsCompanyDetailsStoreService.setCompanyDetailsAdminStatus(
      isAdmin,
    );
    this.profileStoreService.setUserLastLoginTime(lastLoginTime);

    return of(result);
  }

  private redirectToWelcome<T extends Record<string, unknown>>(
    state: T,
  ): Observable<never> {
    this.authService.logout().pipe(take(1)).subscribe();

    return from(
      this.router.navigateByUrl(AppPagesEnum.Welcome, { state }),
    ).pipe(switchMap(() => EMPTY));
  }

  private hasNoValidSubscription(
    userActive: boolean,
    policySubCode: number | null,
  ): boolean {
    return (
      !userActive ||
      policySubCode === UserValidationSubcodes.NoValidSubscription
    );
  }

  private updateTokenExpiry(newExpiryDate: Date): void {
    const existingExpiry = localStorage.getItem(
      AuthTokenConstants.TOKEN_EXPIRY_KEY,
    );

    if (!existingExpiry) {
      this.storeNewExpiry(newExpiryDate);

      return;
    }

    const existingExpiryDate = new Date(existingExpiry);
    this.storeLatestExpiry(existingExpiryDate, newExpiryDate);
  }

  private storeNewExpiry(expiryDate: Date): void {
    this.authService.storeTokenData(expiryDate.toISOString());
  }

  private storeLatestExpiry(existingDate: Date, newDate: Date): void {
    if (newDate > existingDate) {
      this.storeNewExpiry(newDate);
    } else {
      const existingDuration = localStorage.getItem(
        AuthTokenConstants.TOKEN_DURATION_KEY,
      );

      if (existingDuration) {
        localStorage.setItem(
          AuthTokenConstants.TOKEN_DURATION_KEY,
          existingDuration,
        );
      }
    }
  }

  private handleLogoutAndNavigateError(entityType: string): void {
    this.authService.logout().pipe(take(1)).subscribe();
    this.router.navigate([RouteConfig.Error.path], {
      state: {
        message: 'fail',
        entityType,
      },
    });
  }
}
